import { Button } from '@/components/ui/button';
import { ArrowRight, Check, X } from 'lucide-react';
import { CALENDLY_URL } from '@/constants/links';
import { SampleListForm } from './SampleListForm';

const CTAButton = ({ label = "Send me your ICP - I'll build a sample list in 17 minutes" }: { label?: string }) => (
  <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
    <Button className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-6 text-base md:text-lg h-auto">
      {label}
      <ArrowRight className="ml-2 h-5 w-5" />
    </Button>
  </a>
);

interface Row { label: string; old: string; da: string; }

const ComparisonTable = ({ oldHeader, rows }: { oldHeader: string; rows: Row[] }) => (
  <div className="overflow-x-auto rounded-lg border border-border">
    <table className="w-full text-left">
      <thead className="bg-muted">
        <tr>
          <th className="p-4 text-sm font-semibold text-muted-foreground w-1/4"></th>
          <th className="p-4 text-sm font-semibold text-foreground">{oldHeader}</th>
          <th className="p-4 text-sm font-semibold text-primary">Data Aviator</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/30'}>
            <td className="p-4 font-medium text-foreground align-top">{r.label}</td>
            <td className="p-4 text-muted-foreground align-top">{r.old}</td>
            <td className="p-4 text-foreground align-top">{r.da}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const HomepageContent = () => {
  return (
    <>
      {/* Hero */}
      <section className="py-20 md:py-28 border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <p className="text-sm font-semibold tracking-widest text-primary uppercase mb-4">Data Aviator</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
            10 million records verified a day. Every day. So your list doesn't go stale like theirs does.
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mb-10 leading-relaxed">
            Four years ago, five guys sold their advertising business and decided to build the one and only database.
            Today it's 308 million Americans, 50-plus databases ingested continuously, 75 columns wide, verified for
            deliverability and accuracy at a clip of 10 million records a day, on a 60-day cycle. When a source
            changes, it ripples through, we re-verify, and we either keep it or we don't.
          </p>
          <CTAButton />
        </div>
      </section>

      {/* The Problem */}
      <section className="py-20 md:py-24 bg-muted/30 border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">The Problem</h2>
          <p className="text-lg text-muted-foreground mb-10">
            You've bought lists before. You know how this goes.
          </p>
          <ComparisonTable
            oldHeader="Old contact data"
            rows={[
              { label: 'Sources', old: 'One provider, frozen at the point of scrape', da: '50-plus databases, ingested continuously' },
              { label: 'Freshness', old: 'Stale within weeks, no re-check', da: 'Re-verified every 60 days, 10M records/day' },
              { label: 'Deliverability', old: 'Find out when your SDR gets a bounce', da: 'Verified for deliverability before it ever reaches you' },
              { label: 'Transparency', old: 'Black box - no way to see why a contact was included', da: 'Skip-trace score, zero to ten, visible per record' },
              { label: 'Proof', old: 'Buy first, find out later', da: 'Test a real sample before you commit to anything' },
            ]}
          />
          <p className="text-base md:text-lg text-muted-foreground mt-8 leading-relaxed">
            Most single-source providers ship data that's roughly 30% bad on arrival. The reason is simple: each one
            is its own database, frozen in time, while the real world underneath keeps changing. Data Aviator runs
            50-plus sources on top of each other and keeps re-verifying, so it doesn't go stale the way a single-source list does.
          </p>
        </div>
      </section>

      {/* Signal Quality */}
      <section className="py-20 md:py-24 border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Signal Quality: the skip-trace score</h2>
          <p className="text-lg text-muted-foreground mb-10 max-w-3xl">
            Not every record deserves the same confidence. Every profile in Data Aviator carries a skip-trace score,
            zero to ten, so you know what you're looking at before you spend a dollar on outreach.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-lg border border-primary/40 bg-primary/5 p-6">
              <div className="flex items-center gap-2 mb-3">
                <Check className="h-5 w-5 text-primary" />
                <span className="text-sm font-semibold text-primary uppercase tracking-wider">High-confidence record</span>
              </div>
              <p className="text-foreground mb-4">
                Verified email and phone. Corroborated across multiple sources. Recently confirmed active.
              </p>
              <p className="text-2xl font-bold text-foreground">Skip-trace score: 9/10</p>
              <p className="text-muted-foreground">Ready to work.</p>
            </div>
            <div className="rounded-lg border border-border bg-muted/30 p-6">
              <div className="flex items-center gap-2 mb-3">
                <X className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Low-confidence record</span>
              </div>
              <p className="text-foreground mb-4">
                Single source. Email unverified. No recent corroborating signal.
              </p>
              <p className="text-2xl font-bold text-foreground">Skip-trace score: 3/10</p>
              <p className="text-muted-foreground">Needs more before you spend a rep's time on it.</p>
            </div>
          </div>
          <p className="text-base md:text-lg text-muted-foreground mt-8">
            You don't have to take our word for either one. Test the data yourself.
          </p>
        </div>
      </section>

      {/* See It Live */}
      <section className="py-20 md:py-24 bg-muted/30 border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">See It Live: build a list in 17 minutes</h2>
          <p className="text-lg text-muted-foreground mb-10">This is what the platform actually does, not a mockup.</p>
          <ol className="space-y-6 mb-10">
            {[
              ['Tell us your ICP.', 'Industry, company size, title, geography - whatever filters matter to you.'],
              ['We build the list.', 'Pulled from 50-plus sources, cross-verified, scored.'],
              ['You get a CSV, built in about 17 minutes.', 'Skip-trace score included on every record.'],
              ['You test the deliverability yourself.', 'Roll it into whatever you\'re using now and compare.'],
            ].map(([bold, rest], i) => (
              <li key={i} className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">{i + 1}</div>
                <p className="text-foreground text-base md:text-lg leading-relaxed pt-1">
                  <span className="font-semibold">{bold}</span> <span className="text-muted-foreground">{rest}</span>
                </p>
              </li>
            ))}
          </ol>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Last year, building a 1,000-prospect list the old way meant paying an SDR a month of salary and watching
            30% of it bounce. Now it's built in 17 minutes and the deliverability is verified going in.
          </p>
        </div>
      </section>

      {/* Head-to-Head */}
      <section className="py-20 md:py-24 border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-10">Head-to-Head</h2>
          <ComparisonTable
            oldHeader="Legacy single-source providers"
            rows={[
              { label: 'Source count', old: 'Single database', da: '50-plus databases, ingested continuously' },
              { label: 'Verification cadence', old: 'Not disclosed', da: '10M records/day, full re-verify every 60 days' },
              { label: 'Data freshness', old: 'Frozen at point of scrape', da: 'Ripples through automatically when a source changes' },
              { label: 'Per-record confidence', old: 'Not shown', da: 'Skip-trace score, 0-10, visible on every record' },
              { label: 'Try before you buy', old: 'Contract first', da: 'Sample CSV built to your filter, no commitment' },
            ]}
          />
        </div>
      </section>

      {/* Get Started */}
      <section id="get-started" className="py-20 md:py-24 bg-muted/30 border-b border-border scroll-mt-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Get Started</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              No deck required unless you want one. Tell us your ICP and we'll have a real, verified sample in your inbox.
            </p>
          </div>
          <SampleListForm />
        </div>
      </section>

      {/* Roadmap */}
      <section className="py-20 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <p className="text-sm font-semibold tracking-widest text-muted-foreground uppercase mb-3">On the Roadmap</p>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">The Loop</h2>
          <p className="text-lg text-muted-foreground mb-8">
            Not shipped yet - shown here so you know what's coming, not what's live today.
          </p>
          <div className="space-y-5 text-base md:text-lg text-muted-foreground leading-relaxed">
            <p>
              Right now, Data Aviator verifies and re-ingests continuously - that's the 10M-records-a-day, 60-day
              cycle above. The next step is closing the loop all the way: feeding your actual conversion outcomes
              (who converted, who didn't) back into the scoring model so it retrains nightly and gets sharper every
              day you use it, not just every time a source updates.
            </p>
            <p>
              That's a real product build, not a copy change, and it isn't live yet. If it's something you'd use,
              tell us - it's the top of the list for what gets built next.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};
